import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { employeesQueryOptions, shortName } from '@/entities/employee'
import { excursionsQueryOptions, Occupancy } from '@/entities/excursion'
import { ExcursionDialog, type ExcursionDialogState } from '@/features/excursion-editor'
import { addDays, cn, fmtInterval, startOfDay } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Chip } from '@/shared/ui/chip'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'
import { PageBody } from '@/shared/ui/page-body'
import { PageHeader } from '@/shared/ui/page-header'
import { currentMonthPeriod, PeriodPicker, type PeriodValue } from '@/shared/ui/period-picker'
import { Spinner } from '@/shared/ui/spinner'

export const Route = createFileRoute('/_app/excursions/')({
  // Список выходов команды — администраторский. Свой выход гид открывает из
  // расписания: карточка (`/excursions/$excursionId`) ему доступна.
  beforeLoad: ({ context }) => {
    if (!context.session.isAdmin) throw redirect({ to: '/schedule' })
  },
  component: ExcursionsPage,
})

function ExcursionsPage() {
  const [period, setPeriod] = useState<PeriodValue>(() => currentMonthPeriod())
  const [dialog, setDialog] = useState<ExcursionDialogState | null>(null)

  const from = startOfDay(new Date(`${period.from}T00:00:00`))
  const to = addDays(startOfDay(new Date(`${period.to}T00:00:00`)), 1)

  const employeesQuery = useQuery(employeesQueryOptions())
  const excursionsQuery = useQuery(excursionsQueryOptions({ from, to, withBookings: false }))
  const employees = employeesQuery.data ?? []

  return (
    <PageBody>
      <PageHeader
        title="Экскурсии"
        actions={
          <Button
            size="sm"
            onClick={() => setDialog({ excursion: null, slot: defaultSlot(from), guideIds: [] })}
          >
            Новый выход
          </Button>
        }
      />

      <div className="mb-5">
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>

      {excursionsQuery.isLoading ? (
        <Spinner />
      ) : (excursionsQuery.data ?? []).length === 0 ? (
        <EmptyState
          title="За период выходов нет"
          description="Заведите первый — он появится и в расписании команды."
        />
      ) : (
        <ul className="flex flex-col divide-y divide-border-subtle">
          {(excursionsQuery.data ?? []).map((excursion) => (
            <li key={excursion.id}>
              <Link
                to="/excursions/$excursionId"
                params={{ excursionId: excursion.id }}
                className="group flex flex-wrap items-center gap-x-3 gap-y-1 rounded-control px-2 py-3 transition-colors hover:bg-bg-elevated"
              >
                {/* На узком экране строка распадается на карточку: название
                    сверху целиком, дата под ним. Фиксированные 13rem даты
                    съедали половину телефона, и название ужималось до пары
                    букв — а в списке ищут именно по названию.
                    nowrap: ночной выход («8 августа 22:00 – 9 августа 00:00»)
                    иначе ломается на две строки и сбивает ритм списка. */}
                <span className="mono-num order-2 w-full shrink-0 text-xs whitespace-nowrap text-text-tertiary sm:order-none sm:w-60">
                  {fmtInterval(excursion.slot)}
                </span>
                <span
                  className={cn(
                    'order-1 w-full min-w-0 truncate text-sm sm:order-none sm:w-auto sm:flex-1',
                    excursion.state === 'cancelled'
                      ? 'text-text-tertiary line-through'
                      : 'text-text-primary',
                  )}
                >
                  {excursion.title}
                </span>
                <span className="order-3 max-w-40 truncate text-xs text-text-tertiary sm:order-none">
                  {excursion.guideIds
                    .map((id) => {
                      const guide = employees.find((e) => e.id === id)
                      return guide ? shortName(guide) : '—'
                    })
                    .join(', ') || 'без гида'}
                </span>
                {/* Занятость — колонка постоянной ширины и по правому краю:
                    иначе «4/10» и «0/6» пляшут по горизонтали от строки к
                    строке, и глаз перестаёт сравнивать их между собой. */}
                <span className="order-4 shrink-0 text-right sm:order-none sm:w-14">
                  <Occupancy excursion={excursion} />
                </span>
                <span className="order-5 flex shrink-0 items-center gap-2 sm:order-none">
                  {excursion.state === 'cancelled' && <Chip tone="accent">отменён</Chip>}
                  {excursion.state === 'held' && <Chip tone="success">состоялся</Chip>}
                  <Icon
                    name="chevron-right"
                    size={16}
                    className="hidden text-text-tertiary transition-colors group-hover:text-text-secondary sm:block"
                  />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <ExcursionDialog state={dialog} employees={employees} onClose={() => setDialog(null)} />
    </PageBody>
  )
}

function defaultSlot(from: Date) {
  const startsAt = new Date(Math.max(from.getTime(), Date.now()))
  startsAt.setMinutes(0, 0, 0)
  return { startsAt, endsAt: new Date(startsAt.getTime() + 2 * 3_600_000) }
}
